import { useMutation, UseMutationResult } from 'react-query';
import { gql } from '@apollo/client';
import { graphqlClient } from 'bk-web-app';
import { ErrorWithData } from 'task-manager/bench-services-api/ErrorWithData';
import { BusinessService, CreateBusinessServiceInput } from '../types';

const CREATE_CLIENT_SERVICE_MUTATION = gql`
  mutation CreateClientService($input: CreateClientServiceInput!) {
    createClientService(input: $input) {
      id
      client {
        id
      }
      globalService {
        id
      }
      addOn {
        id
      }
      uniqueIdentifier
      effectiveStartDate
      effectiveEndDate
      entityVersion
    }
  }
`;

interface CreateClientServiceResponse {
  createClientService: {
    id: string;
    client: { id: string };
    globalService: { id: string };
    addOn?: { id: string } | null;
    uniqueIdentifier?: string | null;
    effectiveStartDate?: string;
    effectiveEndDate?: string;
    entityVersion?: string;
  };
}

function mapClientServiceToBusinessService(
  clientService: CreateClientServiceResponse['createClientService'],
): BusinessService {
  return {
    businessServiceId: clientService.id,
    businessId: Number(clientService.client.id),
    globalServiceId: clientService.globalService.id,
    uniqueIdentifier: clientService.uniqueIdentifier,
    effectiveStartDate: clientService.effectiveStartDate,
    effectiveEndDate: clientService.effectiveEndDate,
    addOnId: clientService.addOn?.id ?? '',
    createdAt: '',
    createdBy: '',
  };
}

async function createViaGraphQL(input: CreateBusinessServiceInput): Promise<BusinessService> {
  const { data } = await graphqlClient.mutate<CreateClientServiceResponse>({
    mutation: CREATE_CLIENT_SERVICE_MUTATION,
    variables: {
      input: {
        clientId: String(input.businessId),
        globalServiceId: String(input.globalServiceId),
        addOnId: String(input.addOnId),
        uniqueIdentifier: input.uniqueIdentifier,
        effectiveStartDate: input.effectiveStartDate,
        effectiveEndDate: input.effectiveEndDate,
      },
    },
    context: { useV2: true },
  });

  if (!data?.createClientService) {
    throw new Error('Failed to create client service via GraphQL');
  }
  return mapClientServiceToBusinessService(data.createClientService);
}

async function create(apiUrl: string, input: CreateBusinessServiceInput): Promise<BusinessService> {
  if (!apiUrl) throw new Error('Required properties are not set');

  const response: Response = await fetch(`${apiUrl}/business-service`, {
    method: 'POST',
    body: JSON.stringify(input),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = (await response.json()) as Record<string, unknown>;
    throw new ErrorWithData('Failed to create business service', error);
  }
  return (await response.json()) as BusinessService;
}

interface UseCreateBusinessServiceProps {
  handleError: (error: Error) => void;
  useGraphQL?: boolean;
}

export const useCreateBusinessService = ({
  handleError,
  useGraphQL = false,
}: UseCreateBusinessServiceProps): UseMutationResult<
  BusinessService,
  Error,
  CreateBusinessServiceInput
> => {
  const apiUrl = window.BENCH?.auApi?.SETUP_BUSINESS_SERVICE_API_URL || '';

  return useMutation({
    mutationFn: input => (useGraphQL ? createViaGraphQL(input) : create(apiUrl, input)),
    onError: handleError,
  });
};
